import { Box, Typography } from '@mui/material';
import { LoadingScreen, PendingIcon, TimelineCanceledIcon } from '@truvhq/ui';

import { TenantCompanies } from 'features/tenancy/ui/TenantCompanies';
import { useGetCompany, useGetTenantCompanies } from 'hooks/company';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useTenancyContext } from 'shared/lib/tenancy/useTenancyContext';
import { AuthLayoutTemplate } from 'shared/ui/AuthLayoutTemplate';
import { AuthStatusMessage } from 'shared/ui/AuthStatusMessage';

export type UnapprovedCompanyMemberProps = {
  declined: boolean;
};

const maskEmailAddress = (email: string): string => {
  const [localPart, domain] = email.split('@');

  if (!domain) {
    return email;
  }

  if (localPart.length <= 2) {
    const first = localPart.charAt(0);

    return `${first}**@${domain}`;
  }

  const start = localPart.slice(0, 2);
  const end = localPart.slice(-1);

  return `${start}**${end}@${domain}`;
};

export const UnapprovedCompanyMember = ({ declined }: UnapprovedCompanyMemberProps) => {
  const { user } = useAuth();
  const { data: company, isFetching: isCompanyFetching } = useGetCompany();
  const { data: tenantCompanies } = useGetTenantCompanies();
  const { setTenantCompanyId } = useTenancyContext();

  const hasTenantCompanies = tenantCompanies && tenantCompanies.length >= 1;
  const currentTenantCompanyId = user?.company?.id;
  const currentTenantCompany = tenantCompanies?.find((c) => c.id === currentTenantCompanyId);
  const maskedOwnerEmail = currentTenantCompany?.owner_email
    ? maskEmailAddress(currentTenantCompany.owner_email)
    : null;

  if (isCompanyFetching) {
    return <LoadingScreen />;
  }

  const companyName = user?.company?.name || company?.name;

  const title = declined ? 'Your account has been declined' : 'Account pending approval';
  const subtitle = declined ? (
    `Your request to join ${companyName} was not approved. If you have questions, contact an owner or administrator at ${companyName}.`
  ) : (
    <>
      An email has been sent to the company owner
      {maskedOwnerEmail && <strong> {maskedOwnerEmail}</strong>}. Once approved, you’ll receive an email and be able to
      log in to Truv.
    </>
  );
  const icon = declined ? (
    <TimelineCanceledIcon className="h-[40px] w-[40px] ml-[-4px] text-accentRed" />
  ) : (
    <PendingIcon viewBox="0 0 24 24" className="h-[40px] w-[40px] ml-[-8px] text-primary-main" />
  );

  return (
    <AuthLayoutTemplate pageName="UnapprovedCompanyMember">
      <AuthStatusMessage
        icon={icon}
        title={title}
        subtitle={<Typography variant="body1">{subtitle}</Typography>}
        button={
          hasTenantCompanies && (
            <Box className="flex flex-col gap-2 pt-4">
              <Typography variant="body1">You can switch to your other companies:</Typography>
              <TenantCompanies
                tenantCompanies={tenantCompanies}
                onSelectCompany={setTenantCompanyId}
                selectedCompanyId={currentTenantCompanyId}
                variant="light"
                placeholder="Select company"
                popoverDirection="bottom"
                autoSelectFirst={false}
              />
            </Box>
          )
        }
      />
    </AuthLayoutTemplate>
  );
};
